import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import { ConfirmationModal } from '../../../Confirmation'
import {
  DOC_FLOW_UPLOADING_ACTION_CHANGE_TYPE,
  DOC_FLOW_UPLOADING_ACTION_DELETE,
  DOC_FLOW_UPLOADING_MASS_ACTION_CHANGE_TYPE,
  DOC_FLOW_UPLOADING_MASS_ACTION_DELETE,
} from '../../DocFlow.constants'
import { getMassActionsSuccessItemsLength } from '../../DocFlow.utils'
import { getFileUploadedIds } from '../../DocFlow.utils.files'
import { getDocFlowMappingTypeByValue } from '../../DocFlow.utils.mapping'
import { DocFlowChangeTypeModal } from '../../DocFlowChangeTypeModal'
import {
  getUploadingTableNotificationTitle,
  performDocFlowUploadingMassAction,
  updateUploadingItem,
  uploadingPhaseDeleteFile,
} from '../../DocFlowUploading'

import {
  isChangeTypeMassAction,
  isDeleteAction,
  isDeleteMassAction,
} from './useUploadingTableActions.utils'

export function useUploadingTableActions(props) {
  const {
    documentId,
    excludeIds,
    files,
    filterValues,
    pagination,
    selectAllPages,
    selectedIds,
  } = props

  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [actionLoading, setActionLoading] = useState(false)

  function handleSuccessAction(action, notificationProps = EMPTY_OBJECT) {
    props.onCloseModal()
    props.onCleanSelection()

    props.onReload().finally(() => {
      handleAddSuccessNotification({
        title: getUploadingTableNotificationTitle({
          ...notificationProps,
          action,
        }),
      })

      props.onLoadAllFiles()
    })

    if (isDeleteAction(action)) {
      props.onReloadDocFlow()
    }
  }

  function handleErrorAction(title) {
    props.onCloseModal()
    handleAddErrorNotification(title)
  }

  function handleDeleteFile(action, item) {
    return () => {
      setActionLoading(true)

      uploadingPhaseDeleteFile(documentId, item.id)
        .then(() => handleSuccessAction(action))
        .catch(() =>
          handleErrorAction(
            'Notification.DocumentStaplingUploadingActionErrorKey'
          )
        )
        .finally(() => setActionLoading(false))
    }
  }

  function handleMassDelete() {
    setActionLoading(true)

    const ids = getFileUploadedIds(
      selectAllPages
        ? EMPTY_LIST
        : files.filter(item => !selectedIds.includes(item.id))
    )

    props
      .onUpdateDocFlow(
        {
          file_ids: ids,
        },
        null
      )
      .then(() =>
        handleSuccessAction(DOC_FLOW_UPLOADING_MASS_ACTION_DELETE, {
          count: selectedIds.length,
          totalCount: selectedIds.length,
        })
      )
      .finally(() => {
        setActionLoading(false)
      })
  }

  function handleMassAction({ action, value = EMPTY_OBJECT }) {
    setActionLoading(true)

    const id = getMassActionIdParam({ selectAllPages, selectedIds })
    const excludeId = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const params = {
      action,
      all: selectAllPages,
      filters: {
        ...filterValues,
        exclude_id: excludeId,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
      },
      payload: value,
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    props.onCloseModal()

    return performDocFlowUploadingMassAction(documentId, params)
      .then(({ data }) => {
        const successItemsCount = getMassActionsSuccessItemsLength(data)

        handleSuccessAction(action, {
          count: successItemsCount,
          totalCount: countOfItems,
        })

        return data
      })
      .catch(() => {
        handleErrorAction(
          'Notification.DocumentStaplingUploadingMassActionErrorTitleKey'
        )
      })
      .finally(() => {
        setActionLoading(false)
      })
  }

  function handleSubmitChangeType({ item, value }) {
    updateUploadingItem({
      data: value,
      id: documentId,
      itemId: item.id,
    })
      .then(() => {
        props.onCloseModal()

        handleSuccessAction(DOC_FLOW_UPLOADING_ACTION_CHANGE_TYPE)

        props.onReload()
      })
      .catch(() =>
        handleErrorAction(
          'Notification.DocumentStaplingUploadingActionErrorKey'
        )
      )
  }

  function getHandleSubmitChangeType({ action, item }) {
    return ({ value }) =>
      item
        ? handleSubmitChangeType({ item, value })
        : handleMassAction({ action, value })
  }

  function handleDeleteItem(action, item) {
    props.onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DeleteBtnKey'),
        }}
        description={t('DocumentStapling.UploadingModalDeleteTextKey')}
        title={t('Modal.DeleteModalTitlekey')}
        onCancel={props.onCloseModal}
        onSubmit={
          isDeleteMassAction(action)
            ? handleMassDelete
            : handleDeleteFile(action, item)
        }
      />
    )
  }

  function handleOpenChangeTypeModal({ action, item }) {
    props.onSetModal(
      <DocFlowChangeTypeModal
        isSingleItemChanging={isChangeTypeMassAction(action)}
        type={getDocFlowMappingTypeByValue(item?.type, t)}
        onCloseModal={props.onCloseModal}
        onSubmit={getHandleSubmitChangeType({ action, item })}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case DOC_FLOW_UPLOADING_ACTION_DELETE:
        handleDeleteItem(action, item)
        break

      case DOC_FLOW_UPLOADING_ACTION_CHANGE_TYPE:
        handleOpenChangeTypeModal({ action, item })
        break

      default:
        break
    }
  }

  function setMassAction(action) {
    switch (action) {
      case DOC_FLOW_UPLOADING_MASS_ACTION_DELETE:
        handleDeleteItem(action)
        break

      case DOC_FLOW_UPLOADING_MASS_ACTION_CHANGE_TYPE:
        handleOpenChangeTypeModal({ action })
        break

      default:
        return null
    }
  }

  return {
    actionLoading,
    handleOpenChangeTypeModal,
    setItemAction,
    setMassAction,
  }
}
